import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:#080b10; --card:#141920; --border:#1f2a38;
    --blue:#3b82f6; --teal:#00c9a7; --success:#22c55e; --danger:#ef4444;
    --text:#e2e8f4; --muted:#64748b;
    --head:'Barlow Condensed',sans-serif; --body:'DM Sans',sans-serif;
  }
  body { background:var(--bg); color:var(--text); font-family:var(--body); }
  @keyframes spin   { to { transform:rotate(360deg); } }
  @keyframes pop    { from{transform:scale(0);opacity:0} to{transform:scale(1);opacity:1} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
`;

export default function SquareCallback() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const [status, setStatus]   = useState("processing");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const run = async () => {
      // ── Read params — two possible formats ──────────────────────────────
      //
      // Format A: Square POS API (tap-to-pay via Square app)
      //   square-commerce-v1://payment/create?data=BASE64
      //   → Square app redirects back to callback_url?data=BASE64
      //   where data decodes to: { status, transaction_id, error_code, ... }
      //   We also embedded booking_id & tip_cents in the callback_url itself.
      //
      // Format B: Square Payment Links
      //   /square-callback?booking_id=XXX&tip_cents=YYY&checkoutId=ZZZ&transactionId=AAA
      //
      // Format C: Stripe Checkout success_url
      //   /square-callback?booking_id=XXX&tip_cents=YYY&method=stripe

      let booking_id, tip_cents, transaction_id, method, sig;

      const rawData = searchParams.get("data");
      if (rawData) {
        // ── Format A: Square POS ──────────────────────────────────────────
        let posResult;
        try {
          posResult = JSON.parse(atob(rawData));
        } catch(e) {
          setStatus("error");
          setMessage("Could not read Square POS response. Close out the job manually from the dashboard.");
          return;
        }

        if (posResult.status !== "ok") {
          // Payment cancelled or failed in Square app
          const code = posResult.error_code || "CANCELLED";
          setStatus("error");
          setMessage(
            code === "PAYMENT_CANCELED"
              ? "Payment was cancelled. Return to the dashboard to try again."
              : `Square POS error: ${code}. Close out the job manually from the dashboard.`
          );
          return;
        }

        // booking_id and tip_cents were embedded in the callback_url query string
        booking_id     = searchParams.get("booking_id");
        tip_cents      = searchParams.get("tip_cents");
        transaction_id = posResult.transaction_id || null;
        method         = "square_pos";
        sig            = searchParams.get("sig");
      } else {
        // ── Format B / C: Payment Links or Stripe ────────────────────────
        booking_id     = searchParams.get("booking_id");
        tip_cents      = searchParams.get("tip_cents");
        transaction_id = searchParams.get("transactionId") || searchParams.get("checkoutId");
        method         = searchParams.get("method") || "square";
        sig            = searchParams.get("sig");
      }

      if (!booking_id) {
        setStatus("error");
        setMessage("Payment reference missing. Return to the dashboard and close out the job manually.");
        return;
      }

      // ── Call server-side API to close out (uses service role) ────────────
      try {
        const res = await fetch("/api/complete-job", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ booking_id, tip_cents, transaction_id, method, sig }),
        });
        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.error || "Close-out failed.");
        }

        setStatus("success");

      } catch(e) {
        console.error("SquareCallback error:", e);
        setStatus("error");
        setMessage(e.message || "Payment received but job close-out failed. Please complete it from the dashboard.");
      }
    };

    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
        <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:20, padding:40, maxWidth:360, width:"100%", textAlign:"center" }}>

          {status === "processing" && (
            <div style={{ animation:"fadeIn .3s ease" }}>
              <div style={{ width:48, height:48, border:"4px solid rgba(255,255,255,.1)", borderTopColor:"var(--blue)", borderRadius:"50%", animation:"spin .8s linear infinite", margin:"0 auto 20px" }} />
              <div style={{ fontFamily:"var(--head)", fontSize:22, fontWeight:800, letterSpacing:2, marginBottom:8 }}>PROCESSING</div>
              <div style={{ color:"var(--muted)", fontSize:14 }}>Closing out your job...</div>
            </div>
          )}

          {status === "success" && (
            <div style={{ animation:"fadeIn .3s ease" }}>
              <div style={{ fontSize:64, marginBottom:20, animation:"pop .4s cubic-bezier(.175,.885,.32,1.275)" }}>✅</div>
              <div style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2, marginBottom:8, color:"var(--success)" }}>
                PAYMENT RECEIVED
              </div>
              <div style={{ color:"var(--muted)", fontSize:14, marginBottom:28, lineHeight:1.6 }}>
                Job closed out.
              </div>
              <button onClick={() => navigate("/dashboard")}
                style={{ background:"var(--blue)", color:"#fff", border:"none", borderRadius:12, padding:"14px 28px", fontFamily:"var(--body)", fontSize:15, fontWeight:600, cursor:"pointer", width:"100%" }}>
                Back to Dashboard →
              </button>
            </div>
          )}

          {status === "error" && (
            <div style={{ animation:"fadeIn .3s ease" }}>
              <div style={{ fontSize:64, marginBottom:20 }}>⚠️</div>
              <div style={{ fontFamily:"var(--head)", fontSize:24, fontWeight:800, letterSpacing:2, marginBottom:8, color:"var(--danger)" }}>
                SOMETHING WENT WRONG
              </div>
              <div style={{ color:"var(--muted)", fontSize:14, marginBottom:28, lineHeight:1.6 }}>
                {message || "An unexpected error occurred. Close out the job manually from the dashboard."}
              </div>
              <button onClick={() => navigate("/dashboard")}
                style={{ background:"var(--card)", color:"var(--text)", border:"1px solid var(--border)", borderRadius:12, padding:"14px 28px", fontFamily:"var(--body)", fontSize:15, fontWeight:600, cursor:"pointer", width:"100%" }}>
                ← Back to Dashboard
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
